import { Link } from 'react-router-dom';
import type { LocationData } from '../locationData';

interface Props {
  location: LocationData;
}

export default function LocationHero({ location }: Props) {
  const scrollToContact = () => {
    const el = document.getElementById('loc-contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gray-900">
        <img
          src={location.heroImage}
          alt={`Landscaping and snow removal in ${location.name}, ${location.province}`}
          className="w-full h-full object-cover object-top"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/55"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-24">

        {/* Visual Breadcrumb */}
        <nav aria-label="Breadcrumb" className="flex items-center justify-center gap-1.5 text-sm mb-6">
          <Link
            to="/"
            className="flex items-center gap-1 text-white/70 hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line text-base"></i>
            <span>Home</span>
          </Link>
          <span className="text-white/40 select-none">›</span>
          <Link
            to="/locations"
            className="text-white/70 hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            Locations
          </Link>
          <span className="text-white/40 select-none">›</span>
          <span className="text-yellow-400 font-semibold whitespace-nowrap">
            {location.name}
          </span>
        </nav>

        <div className="inline-flex items-center space-x-2 px-4 py-2 bg-yellow-400/20 border border-yellow-400/50 rounded-full text-yellow-300 text-sm font-semibold mb-6">
          <i className="ri-map-pin-line"></i>
          <span>Serving {location.name}, {location.province}</span>
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
          Lawn Care &amp; Snow Removal
          <br />
          <span className="text-yellow-400">in {location.name}</span>
        </h1>

        <p className="text-xl text-white/95 mb-4 max-w-3xl mx-auto font-light">
          {location.description}
        </p>
        <p className="text-base text-white/80 mb-10 max-w-2xl mx-auto">
          One flat monthly rate — no surprises, no extra charges. Licensed &amp; insured.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={scrollToContact}
            className="px-8 py-4 bg-yellow-400 text-gray-900 text-lg font-bold rounded-full hover:bg-yellow-500 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center"
          >
            <i className="ri-file-text-line mr-2 text-xl"></i>
            Get a Free Quote
          </button>
          <a
            href="tel:+16479837924"
            className="px-8 py-4 bg-white text-gray-900 text-lg font-bold rounded-full hover:bg-gray-100 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center"
          >
            <i className="ri-phone-line mr-2 text-xl"></i>
            Call (647) 983-7924
          </a>
          <Link
            to="/"
            className="px-8 py-4 bg-white/10 border border-white/30 text-white text-lg font-semibold rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer inline-flex items-center"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-white/90">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-shield-check-line text-xl text-green-400"></i>
            </div>
            <span className="font-medium">Licensed &amp; Insured</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-star-fill text-xl text-yellow-400"></i>
            </div>
            <span className="font-medium">500+ Happy Clients</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-calendar-check-line text-xl text-cyan-400"></i>
            </div>
            <span className="font-medium">Year-Round Service</span>
          </div>
        </div>
      </div>
    </section>
  );
}
